import { ThreatIcon, type ThreatIconId } from '@/components/primitives/icons/ThreatIcon';
import { OVERLAY_THREAT_GRID_MAX_BLOCK_SIZE } from '@/constants/overlay';
import { overlayThreatImages } from '@/content/data/overlay-threats';

import styles from './OverlayThreatGrid.module.css';

import type { PressureCardId } from '@/content/data/pressure-cards';
import type { CSSProperties } from 'react';

export type OverlayThreatCell = {
  id: PressureCardId;
  icon: ThreatIconId;
  label: string;
  scrim?: 'default' | 'strong' | 'gradient';
};

type OverlayThreatGridProps = {
  threats: readonly OverlayThreatCell[];
};

type ThreatGridDensity = 'pair' | 'triple' | 'quad' | 'six' | 'nine';

function threatGridDensity(count: number): ThreatGridDensity {
  if (count <= 2) return 'pair';
  if (count === 3) return 'triple';
  if (count === 4) return 'quad';
  if (count <= 6) return 'six';
  return 'nine';
}

export function OverlayThreatGrid({ threats }: OverlayThreatGridProps) {
  const density = threatGridDensity(threats.length);
  const gridStyle = {
    '--overlay-threat-grid-max-block-size': OVERLAY_THREAT_GRID_MAX_BLOCK_SIZE,
  } as CSSProperties;

  return (
    <div className={styles.grid} data-density={density} style={gridStyle}>
      {threats.map((threat) => {
        const scrimClass =
          threat.scrim === 'strong'
            ? styles.cellScrimStrong
            : threat.scrim === 'gradient'
              ? styles.cellScrimGradient
              : styles.cellScrim;

        return (
          <div key={threat.id} className={styles.cell}>
            <div className={styles.cellMedia} aria-hidden>
              {/* eslint-disable-next-line @next/next/no-img-element -- decorative threat plate (ADR-004) */}
              <img
                src={overlayThreatImages[threat.id]}
                alt=""
                className={styles.cellImage}
                decoding="async"
              />
              <div className={scrimClass} />
            </div>
            <div className={styles.cellContent}>
              <ThreatIcon id={threat.icon} className={styles.cellIcon} />
              <p className={styles.cellLabel}>{threat.label}</p>
            </div>
          </div>
        );
      })}
    </div>
  );
}
